import {pluralize} from "@/lib/language";

export type DateParts = {
    date: string,
    time: string,
}

export enum DateKind {
    Today = "TODAY",
    Yesterday = "YESTERDAY",
    Tomorrow = "TOMORROW",
    CurrentYear = "CURRENT_YEAR",
    Other = "OTHER",
}

export function dateIntoParts(date: Date): DateParts {
    return {
        date: toIsoDate(date),
        time: toIsoTime(date),
    }
}

// Returns true if end is on the same date, or before 06:00 the day after.
// But only if the start time is at or after 18:00.
export function datesWithinBounds(start: Date, end: Date): boolean {
    // disallow end before start
    if (start.getTime() > end.getTime()) {
        return false;
    }
    if (compareDate(start, end)) {
        return true;
    }
    if (start.getHours() < 18) {
        return false;
    }

    let dayAfter = new Date(start.getTime() + 86400_000);
    let maxDate = new Date(dayAfter.getFullYear(), dayAfter.getMonth(), dayAfter.getDate(), 6);

    return compareDate(end, maxDate) && end.getTime() <= maxDate.getTime();

}

export function formatDate(date: Date): string {
    const kind = dateKind(date);
    if (kind === DateKind.Today) {
        return "i dag";
    } else if (kind === DateKind.Tomorrow) {
        return "i morgen";
    } else if (kind === DateKind.Yesterday) {
        return "i går";
    } else if (kind === DateKind.CurrentYear) {
        return Intl.DateTimeFormat("nb-NO", {
            weekday: "long",
            day: "numeric",
            month: "long",
        }).format(date);
    } else {
        return Intl.DateTimeFormat("nb-NO", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
        }).format(date);
    }
}

export function formatTime(date: Date): string {
    return Intl.DateTimeFormat("nb-NO", {
        timeStyle: "short",
    }).format(date);
}

export function formatSince(date: Date): string {
    const secondsSince = ((new Date()).getTime() - date.getTime()) / 1000;
    if (secondsSince < 1) {
        return "nå"
    }
    if (secondsSince > 86400) {
        const days = Math.floor(secondsSince / 86400);
        return pluralize(days, "dag", "dager");
    }
    if (secondsSince > 3600) {
        const hours = Math.floor(secondsSince / 3600);
        return pluralize(hours, "time", "timer");
    }
    if (secondsSince > 60) {
        const minutes = Math.floor(secondsSince / 60);
        return pluralize(minutes, "minutt", "minutter");
    }
    return pluralize(Math.floor(secondsSince), "sekund", "sekunder");
}

export function isMidnight(date: Date): boolean {
    return date.getHours() === 0 && date.getMinutes() === 0
}

// Analyze the difference between today and a given date,
// and return any proximities found, ranging from today,
// tomorrow, yesterday, current year and other.
export function dateKind(dateTime: Date): DateKind {
    const now = new Date();
    if (compareDate(now, dateTime)) {
        return DateKind.Today;
    }

    // construct a date-only version of the dateTime
    let dateOnly = new Date(dateTime.getFullYear(), dateTime.getMonth(), dateTime.getDate());
    let tomorrow = new Date(dateOnly.getTime() + 86400_000);
    if (compareDate(now, tomorrow)) {
        return DateKind.Yesterday;
    }

    let yesterday = new Date(dateOnly.getTime() - 86400_000);
    if (compareDate(now, yesterday)) {
        return DateKind.Tomorrow;
    }

    if (now.getFullYear() === dateTime.getFullYear()) {
        return DateKind.CurrentYear;
    }

    return DateKind.Other;
}

// Parse a RFC3339 datetime string, discard timezone information and return a new Date object.
export function parseDateTime(s: string): Date {
    const m = s.match(
        /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):.*$/
    );
    if (!m) {
        throw "Invalid date string format"
    }
    let date: Date = new Date();
    date.setFullYear(Number(m[1]));
    date.setMonth(Number(m[2]) - 1);
    date.setDate(Number(m[3]));
    date.setHours(Number(m[4]));
    date.setMinutes(Number(m[5]));
    return date;
}

// Return the naive date from a date object.
export function toIsoDate(date: Date) {
    const year = date.getFullYear();
    const month = zeroPad(date.getMonth() + 1);
    const day = zeroPad(date.getDate());

    return `${year}-${month}-${day}`;
}

// Return the naive time from a date object.
export function toIsoTime(date: Date) {
    const hours = zeroPad(date.getHours());
    const minutes = zeroPad(date.getMinutes());

    return `${hours}:${minutes}`;
}

/**
 * Parses the timestamp from a UUIDv7 string and returns a Date.
 * Assumes a valid RFC 9562 UUIDv7.
 */
export function uuidV7ToDate(uuid: string): Date {
    const hex = uuid.replace(/-/g, "").toLowerCase();

    if (!/^[0-9a-f]{32}$/.test(hex)) {
        throw new Error("Invalid UUID");
    }

    if (hex[12] !== "7") {
        throw new Error("Not a UUIDv7");
    }

    // First 48 bits (12 hex chars) = Unix epoch milliseconds
    const timestampHex = hex.slice(0, 12);
    const timestampMs = Number(BigInt("0x" + timestampHex));

    return new Date(timestampMs);
}

// Returns true if two Date objects have the same date.
function compareDate(date1: Date, date2: Date): boolean {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    )
}

// Zero-pad up to two places.
function zeroPad(n: number) {
    return String(n).padStart(2, "0");
}